import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Wine } from "lucide-react";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/29081a3e-ac58-482c-aff5-9e6ace12b60e/images/719d0b0d6ba3622fdddca2ef2d647ac75dec06bc67800af2abb7dec03ecd00f3.png";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        const res = await login(email, password);
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Bem-vindo de volta");
        const next = params.get("next");
        if (next) navigate(next);
        else if (res.user.role === "admin") navigate("/admin");
        else if (res.user.role === "vendor") navigate("/vendor");
        else navigate("/conta");
    }

    function fillDemo(role) {
        if (role === "admin") {
            setEmail("admin@caseirinho.pt");
            setPassword("Admin@123");
        } else if (role === "vendor") {
            setEmail("valeescuro@caseirinho.pt");
            setPassword("Vendor@123");
        } else {
            setEmail("cliente@caseirinho.pt");
            setPassword("Cliente@123");
        }
    }

    return (
        <main className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-cream">
            {/* LEFT — Cinematic brand panel */}
            <aside className="hidden lg:flex lg:col-span-6 xl:col-span-7 relative bg-wine-deep overflow-hidden">
                <img
                    src={HERO_IMG}
                    alt="Caseirinho"
                    className="absolute inset-0 w-full h-full object-cover kenburns opacity-95"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-wine-deep/45 via-wine-deep/55 to-wine-deep/90" />

                {/* Top */}
                <div className="relative w-full p-12 xl:p-16 flex flex-col justify-between text-cream">
                    <div className="inline-flex items-center gap-3" data-testid="brand-logo">
                        <img
                            src="https://customer-assets.emergentagent.com/job_curated-pt/artifacts/uc0zysi5_c8drzsa3_logo3_dark_badge-removebg-preview%20%281%29.png"
                            alt="O Caseirinho"
                            className="h-28 xl:h-32 w-auto object-contain"
                        />
                    </div>

                    <div className="max-w-lg">
                        <span className="label-overline text-gold-soft">A despensa de Portugal</span>
                        <h1 className="mt-6 font-heading text-5xl xl:text-6xl leading-[0.98] text-shadow-soft">
                            Vinhos e produtos artesanais{" "}
                            <em className="text-gold-soft not-italic">direto de quem faz.</em>
                        </h1>
                        <p className="mt-8 text-cream/75 leading-relaxed">
                            Aceda à sua conta para acompanhar encomendas, gerir o seu Clube e descobrir
                            novas referências dos nossos produtores.
                        </p>
                    </div>

                    <div className="flex items-center justify-between text-xs label-overline text-cream/50">
                        <span>Estabelecido em Portugal</span>
                        <span>v. 1.0 · Premium marketplace</span>
                    </div>
                </div>
            </aside>

            {/* RIGHT — Form panel */}
            <section className="lg:col-span-6 xl:col-span-5 flex items-center justify-center p-6 sm:p-10 lg:p-16">
                <div className="w-full max-w-md">
                    {/* Mobile brand */}
                    <div className="lg:hidden inline-flex items-center gap-2 mb-12">
                        <img
                            src="https://customer-assets.emergentagent.com/job_curated-pt/artifacts/uc0zysi5_c8drzsa3_logo3_dark_badge-removebg-preview%20%281%29.png"
                            alt="O Caseirinho"
                            className="h-20 w-auto object-contain"
                        />
                    </div>

                    <span className="label-overline text-wine">Entrar</span>
                    <h2 className="mt-3 font-heading text-4xl md:text-5xl text-wine-dark leading-[1.05]">
                        Bem-vindo de volta.
                    </h2>

                    <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
                        <Field
                            label="Email"
                            type="email"
                            value={email}
                            onChange={setEmail}
                            testId="login-email"
                            placeholder="o.seu@email.pt"
                        />

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="label-overline text-wine-dark">Palavra-passe</span>
                                <Link
                                    to="/forgot-password"
                                    className="text-[11px] text-wine hover:text-gold-deep transition-colors"
                                    data-testid="forgot-link"
                                >
                                    Esqueceu-se?
                                </Link>
                            </div>
                            <div className="relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="••••••••"
                                    className="w-full bg-white border border-wine-dark/15 rounded-md px-4 py-3.5 pr-12 text-sm focus:border-wine focus:ring-1 focus:ring-wine/20 transition-all outline-none"
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw(!showPw)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-wine-dark/50 hover:text-wine"
                                    data-testid="toggle-password"
                                >
                                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <button
                            disabled={busy}
                            className="btn-wine w-full !py-4 flex items-center justify-center gap-2 group"
                            data-testid="login-submit"
                        >
                            {busy ? (
                                "A entrar…"
                            ) : (
                                <>
                                    Entrar
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                                </>
                            )}
                        </button>
                    </form>

                    <p className="mt-10 text-center text-[11px] text-wine-dark/45">
                        Ao entrar concorda com os{" "}
                        <a href="#" className="underline hover:text-wine">Termos</a> e{" "}
                        <a href="#" className="underline hover:text-wine">Privacidade</a>.
                    </p>
                </div>
            </section>
        </main>
    );
}

function Field({ label, type, value, onChange, testId, placeholder }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required
                placeholder={placeholder}
                className="mt-2 w-full bg-white border border-wine-dark/15 rounded-md px-4 py-3.5 text-sm focus:border-wine focus:ring-1 focus:ring-wine/20 transition-all outline-none"
                data-testid={testId}
            />
        </label>
    );
}

function DemoBtn({ label, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="border border-wine-dark/15 rounded-md py-2.5 px-3 label-overline text-[10px] text-wine-dark hover:border-wine hover:bg-wine hover:text-cream transition-all"
            data-testid={testId}
        >
            {label}
        </button>
    );
}
